import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import {useEffect} from 'react';

import cpicLogo from 'images/icons/cpic.svg';
import dataIcon from 'images/icons/data.svg';
import educationIcon from 'images/icons/education.svg';
import knowledgeIcon from 'images/icons/knowledge.svg';
import toolsIcon from 'images/icons/tools.svg';
import verticalLogo from 'images/logo/brandmark.svg';
import clinpgxHeroLogo from 'images/logo/logo-hero.svg';
import pharmcatIcon from 'images/logo/pharmcat-brandmark.svg';
import pharmdogIcon from 'images/logo/pharmdog-brandmark.svg';

import Button from 'components/Button';
import OnOffButton from 'components/Button/OnOff';
import LetterGlitch from 'components/LetterGlitch';
import SearchBar from 'components/Search/Bar';
import GeneDrugPairIcon from 'components/icons/GeneDrugPair';
import GsiIcon from 'components/icons/Gsi';
import InfoIcon from 'components/icons/Info';
import PediatricIcon from 'components/icons/Pediatric';
import Link from 'components/links/Link';
import Svg from 'components/shared/Svg';
import {useTour} from 'components/tour/hook';
import Types from 'conf/types';
import usePediatricContext from 'context/Pediatric';


export default function HomePage({}) {

  const pediatricContext = usePediatricContext();
  const readyForTour = useTour('home');
  useEffect(() => {
    readyForTour();
  }, [readyForTour]);

  return (
    <div className="home-page">
      <div className="search-ctn">
        <SearchBar site="ClinPGx" />
      </div>

      <div className="container">
        <div className="splash-content row mb-5">
          <div className="col-md-4 splash-graphics">
            <Svg src={clinpgxHeroLogo} ariaLabel="ClinPGx Logo" className="splash-logo" />
          </div>
          <div className="col-md-8 splash-copy">
            <h1>
              <span className="tagline">The evolution of PharmGKB</span>
            </h1>
            <p>
              ClinPGx is a comprehensive clinical pharmacogenomic (PGx) resource created to support and expand PGx
              knowledge, implementation and education. It integrates the PharmGKB, CPIC and PharmCAT projects, with
              additional features and content to come. Our goals are to make clinical PGx accessible and facilitate its
              integration with genomic medicine. ClinPGx is an affiliate grant of the Clinical Genome Resource
              (<Link href="https://clinicalgenome.org">ClinGen</Link>).
            </p>
            <div className="splash-links">
              <div><Button className="btn-lg btn-primary" href="/page/overview">What is ClinPGx?</Button></div>
              <div><Button className="btn-lg btn-outline-primary" href="/whatIsPharmacogenomics">What is Pharmacogenomics?</Button></div>
              <div><Button className="btn-lg btn-outline-primary" href="https://blog.clinpgx.org">ClinPGx Blog</Button></div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="home-grid">
          <div className="glass-blur">
            <div>
              <h2>
                <FontAwesomeIcon icon={['fal', 'table']} /> Precision Guidance
              </h2>
            </div>

            <div className="actionable-row">
              <div>
                <p className="mb-0">Access drug prescribing recommendations from DNA test results.</p>
              </div>
              <div className="row">
                <div className="col-lg d-flex flex-column">
                  <Link href="https://pharmdog.clinpgx.org"><h3><Svg src={pharmdogIcon} ariaHidden={true} inline={true} className="home-block__icon" />PharmDOG</h3></Link>
                  <div className="flex-grow-1">Get <strong>focused</strong> PGx guidance on all devices</div>
                </div>
                <div className="col-lg d-flex flex-column">
                  <Link href="/genotype"><h3><GsiIcon />GSI</h3></Link>
                  <div className="flex-grow-1"><strong>Compare</strong> PGx guidance from different sources</div>
                </div>
                <div className="col-lg d-flex flex-column">
                  <Link href="https://pharmcat.clinpgx.org"><h3><Svg src={pharmcatIcon} ariaHidden={true} inline={true} className="home-block__icon" />PharmCAT</h3></Link>
                  <div className="flex-grow-1">Translate <strong>VCF data</strong> into PGx guidance</div>
                </div>
              </div>

              <div>
                <p className="mb-0">Learn more about genes and drugs with prescribing guidance.</p>
              </div>
              <div className="row">
                <div className="col-lg d-flex flex-column">
                  <Link href="/pgxGenes"><h3 className="color__gene"><Svg src={Types.gene.icon} inline={true} className="home-block__icon" />Genes</h3></Link>
                  <p className="flex-grow-1">See actionable PGx genes</p>
                </div>
                <div className="col-lg d-flex flex-column">
                  <Link href="/prescribingInfo"><h3 className="color__drug"><Svg src={Types.chemical.icon} inline={true} className="home-block__icon" />Drugs</h3></Link>
                  <p className="flex-grow-1">See drugs affected by PGx</p>
                </div>
                <div className="col-lg d-flex flex-column">
                  <Link href="/pairs"><h3><span className="home-block__icon"><GeneDrugPairIcon /></span>Pairs</h3></Link>
                  <p className="flex-grow-1">Explore gene-drug associations</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-blur">
            <div><h2><Svg src={verticalLogo} inline={true} ariaLabel="ClinPGx Logo" className="vertical-logo" />Curated PGx Evidence</h2></div>
            <p>Gene-drug and allele-drug associations manually curated by ClinPGx scientists.</p>

            <div className="row">
              <div className="col-lg">
                <div>
                  <Link href="/guidelineAnnotations"><h3 className="color__guideline"><Svg src={Types.guidelineAnnotation.icon} inline={true} className="home-block__icon" />Guidelines</h3></Link>
                  <p>Annotated PGx-based clinical guidelines</p>
                </div>
              </div>

              <div className="col-lg">
                <div>
                  <Link href="/labelAnnotations"><h3 className="color__label"><Svg src={Types.labelAnnotation.icon} inline={true} className="home-block__icon" />Drug Labels</h3></Link>
                  <p className="mb-1">Annotated PGx from drug labels</p>
                </div>
                <div>
                  <ul className="list-unstyled mb-0">
                    <li><em>See also:</em> <Link href="/fdaLabelAnnotations">FDA Labels</Link></li>
                  </ul>
                </div>
              </div>

              <div className="col-lg">
                <div>
                  <Link href="/summaryAnnotations"><h3 className="color__literature"><Svg src={Types.literature.icon} inline={true} className="home-block__icon" />Literature</h3></Link>
                  <p>Allele-drug associations from published literature</p>
                </div>
              </div>

              <div className="col-lg">
                <div>
                  <Link href="/pathways"><h3 className="color__pathway"><Svg src={Types.pathway.icon} inline={true} className="home-block__icon" />Pathways</h3></Link>
                  <p>Evidence-based drug pathway diagrams</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-blur">
            <div>
              <h2 className="cpic-header">
                <Svg src={cpicLogo} inline={true} ariaLabel="CPIC Logo" className="cpic-logo" />
                <span className="color__red">CPI</span><span className="color__blue">C</span>
              </h2>
              <p>The Clinical Pharmacogenetics Implementation Consortium creates evidence-based gene/drug clinical practice guidelines enabling clinicians to use PGx in patient care.</p>
            </div>
            <div className="row">
              <div className="col"><Link href="/cpic/guidelines">Guidelines</Link></div>
              <div className="col"><Link href="/page/pgxGeneRef">Gene Tables</Link></div>
              <div className="col"><Link href="https://cpicpgx.org">cpicpgx.org</Link></div>
              <div className="col"><Link href="https://github.com/cpicpgx/cpic-data">CPIC DB &amp; API</Link></div>
            </div>
          </div>

          <div className="glass-blur">
            <div>
              <h2><PediatricIcon className="home-block__icon" />Pediatrics</h2>
              <p>
                Turn on the Pediatric Focus to highlight pediatrics on ClinPGx. Read about pediatrics on ClinPGx through the <Link href="/pediatric/dashboard">Pediatric Dashboard</Link>. See <Link href="/page/PedsIntro">Pediatric Help</Link> for more information.
              </p>
              <OnOffButton
                label="Pediatric Focus"
                on={pediatricContext.isPediatricMode}
                stateful={false}
                actionHandler={pediatricContext.togglePediatricMode}
                tabIndex={0}
                className="onOffButton__inline"
              />

              <div className={clsx('alert', 'alert-info', {'d-none': !pediatricContext.isPediatricMode})}>
                <InfoIcon size="2x" className="float-left mr-3" /> With pediatric focus on, you will see pediatric-specific information on gene, drug, and other pages.
                <p>You can disable it in the &quot;Focus&quot; menu at the top of every page.</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="container home-section">
        <section style={{height: '34px', marginBottom: '6em'}} className="d-none d-lg-block d-xl-block">
          <LetterGlitch outerVignette={false} glitchSpeed={150} />
        </section>
        <div className="row">
          <div className="col-lg-4 mb-3">
            <h2 className="section-heading resources">
              <Svg src={knowledgeIcon} ariaHidden={true} inline={true} className="icon--knowledge" /> Knowledge
            </h2>
            <div className="resource-center"><a href="/page/cancerPgx" className="font-weight-bold">Cancer Pharmacogenetics</a></div>
            <div className="resource-center"><a href="/page/DrugUseAndAddiction" className="font-weight-bold">PGx in Drug Use and Addiction</a></div>
            <div className="resource-center"><a href="/alleleFrequency" className="font-weight-bold">Allele Frequency</a></div>
          </div>
          <div className="col-lg-4 mb-3">
            <h2 className="section-heading resources">
              <Svg src={toolsIcon} ariaHidden={true} inline={true} className="icon--tools" /> Implementation
            </h2>
            <div className="resource-center"><a href="/ampAllelesToTest" className="font-weight-bold">AMP Alleles to Test</a></div>
            <div className="resource-center"><a href="/alleleFunctions" className="font-weight-bold">Allele Function</a></div>
            <div className="resource-center"><a href="/fdaPgxAssociations" className="font-weight-bold">FDA PGx Associations</a></div>
          </div>
          <div className="col-lg-4 mb-3">
            <h2 className="section-heading resources">
              <Svg src={educationIcon} ariaHidden={true} inline={true} className="icon--education" /> Education
            </h2>
            <div className="resource-center"><a href="/page/educationalResources" className="font-weight-bold">Educational Resources</a></div>
            <div className="resource-center"><a href="/page/biogeographicalGroups" className="font-weight-bold">Biogeographical Groups for PGx</a></div>
            <div className="resource-center"><a href="/page/curationTips" className="font-weight-bold">PGx Publication Tips</a></div>
          </div>
        </div>
      </div>

      <div className="container home-section text-center">
        <section style={{height: '34px', marginBottom: '6em'}} className="d-none d-lg-block d-xl-block">
          <LetterGlitch outerVignette={false} glitchSpeed={150} />
        </section>
        <h2>
          <Svg src={dataIcon} ariaHidden={true} inline={true} className="icon--data" /> PGx Data
        </h2>
        <p>ClinPGx curates and distributes many different types of information in multiple formats.</p>
        <div className="d-flex flex-row justify-content-center" style={{gap: '1em'}}>
          <div className=""><Link href="/downloads">Downloads</Link></div>
          <div className=""><Link href="https://api.clinpgx.org">API Docs</Link></div>
          <div className=""><Link href="/statistics">Statistics</Link></div>
        </div>
      </div>
    </div>
  );
}
